import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useShallow } from 'zustand/react/shallow';

import { vabsInternalClientQuery } from '@/entities/payment';

import {
  ArrowLeft,
  Globus,
  LogoIconSmall,
  TapIcon,
} from '@/shared/assets';
import { debounce } from '@/shared/lib';
import {
  AvatarWithIndicator,
  Button,
  DashboardDialogProps,
  ModalCard,
  SearchInput,
  Skeleton,
  Typography,
} from '@/shared/ui';

import { MoneyFlowType } from '../../../../model/types';
import { useSendMoney } from '../../../../model/useSendMoney';

function checkString(input: string): 'email' | 'phone' | 'unknown' {
  if (!input) return 'unknown';
  // Simple email regex
  const emailPattern = /^[\w.-]+@[\w.-]+\.\w+$/;

  // Simple phone regex: allows +, digits, spaces, dashes, parentheses
  const phonePattern = /^\+?\d[\d\s\-()]{7,}$/;

  if (emailPattern.test(input)) {
    return 'email';
  } else if (phonePattern.test(input)) {
    return 'phone';
  } else {
    return 'unknown';
  }
}

const FindClient: React.FC<{
  setStep: (step: MoneyFlowType) => void;
}> = ({ setStep }) => {
  const [search, setSearch] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    updateFormData,
    data: updatedData,
    resetData,
  } = useSendMoney(useShallow((state) => state));

  useEffect(() => {
    if (updatedData) {
      setEmail(updatedData.innerEmail || '');
      setPhoneNumber(updatedData.innerPhone || '');
    }
  }, [updatedData]);

  useEffect(() => reset, []);

  const { data, isLoading: isLoadingQuery } = useQuery({
    queryKey: ['inner-client', email, phoneNumber],
    queryFn: () => vabsInternalClientQuery({ email, phoneNumber }),
    enabled: !!(email || phoneNumber),
    select: (data) => data[0],
  });

  useEffect(() => {
    setIsLoading(isLoadingQuery);
  }, [isLoadingQuery]);

  const debounceSearchHandler = debounce((value: string) => {
    const result = checkString(value);
    if (result === 'unknown') {
      setEmail('');
      setPhoneNumber('');
    }
    if (result === 'email') {
      setEmail(value.trim());
    }
    if (result === 'phone') {
      setPhoneNumber(value.trim());
    }
    setIsLoading(false);
  }, 2000);

  const onSearch = (value: string) => {
    setIsLoading(true);
    setSearch(value);
    setEmail('');
    setPhoneNumber('');
    debounceSearchHandler(value);
  };

  const reset = () => {
    setSearch('');
    setEmail('');
    setPhoneNumber('');
  };

  const onClientClick = () => {
    updateFormData({
      isInner: true,
      innerAccountId: data?.accountId,
      clientId: data?.clientId,
      companyName: data?.companyName,
      innerAvailableCurrencies: data?.availableCurrencies.map((i) => i.id),
      innerEmail: email,
      innerPhone: phoneNumber,
    });
    setStep('accounts');
  };

  return (
    <div className="mt-1 flex h-full w-full flex-col gap-y-1">
      <div className="flex-1">
        <div className="w-full px-4 pb-4">
          <SearchInput
            value={search}
            containerClassName="rounded-[16px] h-[48px]"
            placeholder="Email or mobile number"
            onSearch={onSearch}
            onClear={() => {
              reset();
            }}
            isClosable
          />
        </div>
        <div className="relative flex flex-1 flex-col gap-2 pt-3">
          <div>
            {isLoading ? (
              <div className="flex items-center gap-4 px-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-5 w-[80%]" />
                  <Skeleton className="mt-1 h-5 w-[50%]" />
                </div>
              </div>
            ) : (
              <div>
                {data ? (
                  <Button
                    className="flex w-full items-center justify-start gap-x-4 !px-4 !py-2 text-start"
                    variant="clean"
                    onClick={onClientClick}
                  >
                    <AvatarWithIndicator
                      avatarClassName="[&_svg]:!w-10 [&_svg]:!h-10"
                      indicator={
                        <LogoIconSmall className="bg-base-500 [&_svg]:!h-5 [&_svg]:!w-5" />
                      }
                    />

                    <div className="flex flex-col gap-y-1">
                      <Typography>{data.companyName}</Typography>
                      <Typography variant="14-r" color="text-base-100">
                        {email || phoneNumber}
                      </Typography>
                    </div>
                  </Button>
                ) : (
                  Boolean(search?.trim()) && (
                    <Typography
                      className="flex justify-center py-4 text-base-100"
                      variant="14-r"
                    >
                      Nothing was found
                    </Typography>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!data && !isLoading && !search?.trim() && (
        <ModalCard className="relative bottom-0 py-5">
          <div className="flex w-full items-start gap-2">
            <TapIcon className="!h-5 !w-5 text-base-100 shrink-0" />
            <Typography className="text-base-100" variant="14-r">
              Use email or full number with + country code (no spaces). Partial
              search won’t work
            </Typography>
          </div>
          <div className="flex w-full items-start gap-2">
            <Globus className="!h-5 !w-5 text-base-100 shrink-0" />
            <Typography className="text-base-100" variant="14-r">
              Recipient hidden? Ask them to update Profile → Security & Privacy
              → Find me by
            </Typography>
          </div>
        </ModalCard>
      )}
    </div>
  );
};

interface FindClientProps {
  setStep: (step: MoneyFlowType) => void;
  onBack: () => void;
  isFirst: boolean;
}

export const findClientProps = ({
  setStep,
  onBack,
  isFirst,
}: FindClientProps): DashboardDialogProps => {
  return {
    title: <p className="flex flex-col items-center"><span>Search</span><span>on FCB</span></p>,
    content: <FindClient setStep={setStep} />,
    animationProps: {
      id: 'choose',
      isFirst: isFirst,
    },
    modalProps: {
      classNames: {
        wrapper: 'h-dvh'
      },
    },
    disableOutsideClick: !isFirst,
    serviceButton: !isFirst
      ? {
        icon: <ArrowLeft className="min-h-6 min-w-6" />,
        onClick: () => {
          onBack();
        },
      }
      : undefined,
    hideCloseButton: !isFirst,
  };
};
